import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "rain-on-glass";

const DEFAULT_BG_COOL = "#0f172a";
const DEFAULT_BG_WARM = "#1e293b";
const DEFAULT_BOKEH = "#fde68a";
const DEFAULT_DROP = "#e2e8f0";

// Background bokeh — soft, blurred light blobs that sell the "city through
// a wet window" feel. Generated once per resize and cached as a separate
// canvas so the per-frame cost is just a single drawImage.
const BOKEH_COUNT = 9;
const BOKEH_BLUR_PX = 28;

// Static condensation — many tiny droplets sprinkled across the canvas.
// They sit still, scattering light, until a falling drop sweeps through
// and absorbs them.
const STATIC_DENSITY = 0.0012; // drops per pixel²
const STATIC_MIN_R = 1.0;
const STATIC_MAX_R = 3.5;

// Falling drops — spawn at the top, accelerate downward, absorb static
// drops they intersect, leave a clear "trail" that erases the condensation
// path behind them (giving the iconic clear streak through fogged glass).
const SPAWN_INTERVAL_MS = 280;
const SPAWN_JITTER_MS = 250;
const FALL_GRAVITY = 0.18;     // px/frame² acceleration
const MIN_FALL_R = 4;          // px — smallest drop that will fall
const MAX_FALL_R = 11;         // px — largest spawned drop
const MAX_CONCURRENT = 22;
const TRAIL_WIDTH_FRAC = 0.7;  // trail width = drop radius * this

function rand(min, max) { return min + Math.random() * (max - min); }

export default function RainOnGlass({ colors = [], style }) {
  const canvasRef = useRef(null);
  const bokehCanvasRef = useRef(null);
  const trailCanvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_BG_COOL, DEFAULT_BG_WARM, DEFAULT_BOKEH, DEFAULT_DROP]);
  const staticDropsRef = useRef([]);
  const fallingRef = useRef([]);
  const lastSpawnRef = useRef(0);
  const nextSpawnDelayRef = useRef(SPAWN_INTERVAL_MS);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG_COOL),
      pickColor(colors, 1, DEFAULT_BG_WARM),
      pickColor(colors, 2, DEFAULT_BOKEH),
      pickColor(colors, 3, DEFAULT_DROP),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const buildBokehLayer = () => {
      // Pre-render the blurred backdrop into an offscreen canvas, so each
      // frame just blits it instead of recomputing blurs.
      const off = document.createElement("canvas");
      off.width = Math.floor(width * dpr);
      off.height = Math.floor(height * dpr);
      const octx = off.getContext("2d");
      if (!octx) return;
      octx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const [bgCool, bgWarm, bokeh] = colorsRef.current;

      // Diagonal base gradient — cool top-left, warmer bottom-right.
      const grad = octx.createLinearGradient(0, 0, width, height);
      grad.addColorStop(0, bgCool);
      grad.addColorStop(1, bgWarm);
      octx.fillStyle = grad;
      octx.fillRect(0, 0, width, height);

      // Soft circular highlights — heavily blurred so they look like
      // out-of-focus distant lights through a foggy window.
      octx.filter = `blur(${BOKEH_BLUR_PX}px)`;
      for (let i = 0; i < BOKEH_COUNT; i += 1) {
        const r = rand(width * 0.10, width * 0.22);
        const x = rand(0, width);
        const y = rand(0, height);
        const a = rand(0.18, 0.42);
        const radial = octx.createRadialGradient(x, y, 0, x, y, r);
        radial.addColorStop(0, rgba(bokeh, a));
        radial.addColorStop(1, rgba(bokeh, 0));
        octx.fillStyle = radial;
        octx.beginPath();
        octx.arc(x, y, r, 0, Math.PI * 2);
        octx.fill();
      }
      octx.filter = "none";

      bokehCanvasRef.current = off;
    };

    const buildTrailLayer = () => {
      // The trail canvas accumulates the "clear streaks" left by falling
      // drops. We never clear it — drops erase to transparent and we
      // composite this over the bokeh, so the streaks reveal the sharper
      // light behind the condensation.
      const off = document.createElement("canvas");
      off.width = Math.floor(width * dpr);
      off.height = Math.floor(height * dpr);
      const tctx = off.getContext("2d");
      if (!tctx) return;
      tctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Start fully covered with semi-opaque white "fog" representing
      // condensation. Falling drops carve transparent paths through this
      // layer using `destination-out`.
      const [bgCool, , , drop] = colorsRef.current;
      // Slight tint so the fog isn't pure white. Mix with bg.
      tctx.fillStyle = rgba(drop, 0.0); // start transparent — easier
      tctx.fillRect(0, 0, width, height);
      // Sprinkle a faint global fog by drawing many small alpha dots:
      // (We rely on the static drops drawn each frame to *be* the fog,
      //  so the trail layer just needs to be the carved trail buffer.)
      // We'll keep this layer transparent and use it purely as a mask
      // that grows over time — we draw white into it where drops have
      // passed, and we'll subtract that mask from the static drops.

      trailCanvasRef.current = off;
    };

    const seedStaticDrops = () => {
      const count = Math.floor(width * height * STATIC_DENSITY);
      const arr = new Array(count);
      for (let i = 0; i < count; i += 1) {
        arr[i] = {
          x: rand(0, width),
          y: rand(0, height),
          r: rand(STATIC_MIN_R, STATIC_MAX_R),
          alive: true,
        };
      }
      staticDropsRef.current = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildBokehLayer();
      buildTrailLayer();
      seedStaticDrops();
      fallingRef.current = [];
      lastSpawnRef.current = 0;
    };

    const spawnDrop = () => {
      if (fallingRef.current.length >= MAX_CONCURRENT) return;
      fallingRef.current.push({
        x: rand(width * 0.04, width * 0.96),
        y: rand(-20, -4),
        vy: rand(0.4, 1.0),
        r: rand(MIN_FALL_R, MAX_FALL_R),
      });
    };

    const tick = (ts) => {
      const [, , , drop] = colorsRef.current;

      // 1. Background bokeh layer (pre-rendered).
      const bokeh = bokehCanvasRef.current;
      if (bokeh) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.drawImage(bokeh, 0, 0);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }

      // 2. Static condensation droplets — soft white circles. Drawn
      // every frame against the bokeh so the bokeh shows through tinted.
      // We respect the per-drop `alive` flag so absorbed drops disappear.
      const sd = staticDropsRef.current;
      ctx.fillStyle = rgba(drop, 0.55);
      for (let i = 0; i < sd.length; i += 1) {
        const s = sd[i];
        if (!s.alive) continue;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // 3. Spawn new falling drops.
      if (!lastSpawnRef.current) lastSpawnRef.current = ts;
      if (ts - lastSpawnRef.current > nextSpawnDelayRef.current) {
        spawnDrop();
        lastSpawnRef.current = ts;
        nextSpawnDelayRef.current = SPAWN_INTERVAL_MS + rand(0, SPAWN_JITTER_MS);
      }

      // 4. Update + render falling drops, plus their trails.
      const falling = fallingRef.current;
      for (let i = falling.length - 1; i >= 0; i -= 1) {
        const f = falling[i];
        const prevY = f.y;
        f.vy += FALL_GRAVITY;
        f.y += f.vy;

        // Trail: thin tinted streak from prevY → y. We draw it at low
        // alpha behind the drop so it reads as a still-wet path. We also
        // mark any static drops along that path as absorbed.
        ctx.strokeStyle = rgba(drop, 0.85);
        ctx.lineCap = "round";
        ctx.lineWidth = f.r * TRAIL_WIDTH_FRAC;
        ctx.beginPath();
        ctx.moveTo(f.x, prevY);
        ctx.lineTo(f.x, f.y);
        ctx.stroke();

        // Absorb nearby static drops along the swept segment. We approximate
        // by checking each static drop against the bounding band — cheap
        // and good enough at these counts.
        const minY = Math.min(prevY, f.y) - f.r;
        const maxY = Math.max(prevY, f.y) + f.r;
        const xMin = f.x - f.r;
        const xMax = f.x + f.r;
        for (let j = 0; j < sd.length; j += 1) {
          const s = sd[j];
          if (!s.alive) continue;
          if (s.x < xMin || s.x > xMax) continue;
          if (s.y < minY || s.y > maxY) continue;
          // Closer test: distance from the swept line segment.
          const dx = s.x - f.x;
          if (Math.abs(dx) <= f.r + s.r) {
            s.alive = false;
            // Drop grows slightly as it absorbs water → realistic
            // accumulation effect.
            f.r = Math.min(MAX_FALL_R + 2, f.r + s.r * 0.05);
          }
        }

        // Draw the drop itself: bright core + soft outer ring for the
        // refractive look.
        const haloR = f.r * 1.8;
        const halo = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, haloR);
        halo.addColorStop(0, rgba(drop, 0.85));
        halo.addColorStop(1, rgba(drop, 0));
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(f.x, f.y, haloR, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = rgba(drop, 0.95);
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fill();

        // Tiny specular highlight — just a smaller offset white circle.
        ctx.fillStyle = "rgba(255, 255, 255, 0.55)";
        ctx.beginPath();
        ctx.arc(f.x - f.r * 0.35, f.y - f.r * 0.35, f.r * 0.28, 0, Math.PI * 2);
        ctx.fill();

        if (f.y - f.r > height + 20) {
          falling.splice(i, 1);
        }
      }

      // 5. Re-seed static drops opportunistically: when too many have been
      // absorbed we re-sprinkle to keep the glass looking fogged. We do
      // this gradually (a few per frame) to avoid a visible "snap".
      if (sd.length > 0) {
        let dead = 0;
        for (let i = 0; i < sd.length; i += 1) if (!sd[i].alive) dead += 1;
        if (dead > sd.length * 0.4) {
          let restored = 0;
          for (let i = 0; i < sd.length && restored < 6; i += 1) {
            if (!sd[i].alive) {
              sd[i].x = rand(0, width);
              sd[i].y = rand(0, height);
              sd[i].r = rand(STATIC_MIN_R, STATIC_MAX_R);
              sd[i].alive = true;
              restored += 1;
            }
          }
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
